import { styled } from '@linaria/react';
import { Divider, Group, Skeleton } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Measure } from '../../../api/work-periods-client/work-periods-client.type.ts';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { formatDate } from '../../../helpers/date-time-helpers/formatting/formatting.ts';
import { safeParseDateInput } from '../../../helpers/date-time-helpers/parsing/parsing.ts';
import { exportChart } from '../../../helpers/image-downloader/image-downloader';
import { useGlobalStore } from '../../../store/global-store/global-store.ts';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import { baseWhite, secondaryBase, skyDark } from '../../../styles/design-tokens.ts';
import { Button } from '../../../ui-library/button/button.tsx';
import { Icon } from '../../../ui-library/icon/icon.tsx';
import { Select } from '../../../ui-library/select/select.tsx';
import { Text } from '../../../ui-library/typography/typography.tsx';
import { FloraButton } from '../../flora/flora-button.tsx';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import { WorkPeriodChartHeader } from '../components/work-period-chart-header.tsx';
import { WorkPaceChartType } from '../work-periods.type.ts';
import type { PaceChartDataPoint } from '../work-periods.type.ts';
import { formatWorkPaceDataForFlora, getWorkPaceFloraPromptKey } from '../helpers/single-assessment-flora.helpers.ts';
import { useFlowByPaceData, useWorkPeriodsTasksData } from '../hooks/work-periods-calculation.hooks';
import { PhaseBox } from '../work-phase-distribution/phase-box.tsx';
import { PhaseBoxContainer } from '../work-phase-distribution/work-phase-distribution-chart-phases.tsx';
import {
  filterPaceData,
  getScopeMetadata,
  getTasksCompleteMetadata,
  getWorkPeriodBurnedMetadata,
  transformForPaceChart,
} from './pace-section.helpers.ts';
import { WorkPaceChart } from './work-pace-chart.tsx';

interface WorkPaceProps {
  onTasksChange: (tasks: TaskRecord[], title?: string, isLoading?: boolean) => void;
  tasksOpen: boolean;
}

/**
 * Container for the Work Pace tab under `/application/work-periods`. Renders
 * the static Burnt / Done / Scope summary cards, the chart-type pill switcher,
 * the date picker, and the line chart. Anchors all metric reads to a
 * lookup-safe `cardDate` so trailing future-zero rows in chartData can never
 * leak into the summary, while the picker subtitle and Flora payload follow
 * the user's literal date pick.
 *
 * @param {WorkPaceProps} props - Container props
 * @param {(tasks: TaskRecord[], title?: string, isLoading?: boolean) => void} props.onTasksChange -
 *   Callback invoked when the Done drill-in completes; the parent route owns
 *   the task drawer state and rendering.
 * @param {boolean} props.tasksOpen - Whether the parent task drawer is open;
 *   drives the `isSelected` outline on the Done PhaseBox.
 * @returns {JSX.Element} The Work Pace tab UI
 */
export const WorkPace = ({ onTasksChange, tasksOpen }: WorkPaceProps) => {
  const i18n = useTranslation();
  const downloadRef = useRef<HTMLDivElement>(null!);
  const { selectedWorkPeriods, selectedMetric } = useWorkPeriodStore();
  const selectedWorkPeriod = useMemo(() => selectedWorkPeriods[0], [selectedWorkPeriods]);
  const [chartType, setChartType] = useState<WorkPaceChartType>(WorkPaceChartType.BurnUp);
  const [clickedPhaseBox, setClickedPhaseBox] = useState<boolean>(false);
  /**
   * User-driven date selection from the picker. When null, the rendered date
   * falls back to `anchorDate` (today clamped to the work-period range). The
   * value is invalidated synchronously when the work period changes via the
   * `trackedWorkPeriodId` reconciliation below, so the new period's anchor
   * wins on the same render — no useEffect / one-frame flash.
   */
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  /**
   * Tracks the work-period id this selection belongs to. When the active
   * work period changes we update this AND clear `selectedDate` in the same
   * render pass (React's "adjusting state when a prop changes" pattern —
   * https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
   */
  const [trackedWorkPeriodId, setTrackedWorkPeriodId] = useState<string | undefined>(selectedWorkPeriod?.id);
  if (trackedWorkPeriodId !== selectedWorkPeriod?.id) {
    setTrackedWorkPeriodId(selectedWorkPeriod?.id);
    setSelectedDate(null);
  }
  const useAI = useGlobalStore((state) => state.useAI);

  const { data, isLoading } = useFlowByPaceData();
  // Filter first: `transformForPaceChart` strips `tasks_`/`points_` prefixes into shared fields, so unfiltered points_* overwrites tasks_*.
  const chartData = useMemo(
    () => (data ? transformForPaceChart(filterPaceData(data, selectedMetric)) : []),
    [data, selectedMetric],
  );

  /**
   * Static reference date for the Burnt / Done / Scope summary cards.
   * Resolves to today, clamped to the work-period bounds (falling back to
   * the chart-data range when start/end dates aren't on the selected work
   * period). Guarantees the returned string exists in chartData by snapping
   * to the latest data-point on or before the clamped date when an exact
   * match isn't available — so the metric helpers never short-circuit to
   * zero/empty results from a missing-date lookup.
   *
   * Note: chartData.date values and the work-period start/end fields are
   * YYYY-MM-DD strings; lexicographic comparison is intentional and
   * format-stable as long as that contract holds.
   */
  const anchorDate = useMemo<string | null>(() => {
    if (!chartData.length) return null;
    const sortedDates = chartData
      .map((point) => point.date)
      .filter((date): date is string => Boolean(date))
      .sort();
    if (!sortedDates.length) return null;
    const dataFirstDate = sortedDates[0];
    const dataLastDate = sortedDates[sortedDates.length - 1];
    // safeParseDateInput normalizes ISO-with-Z and YYYY-MM-DD into a Dayjs
    // anchored to the user's local day, avoiding off-by-one when the
    // backend returns timestamps like "2026-05-04T00:00:00Z".
    const parsedStart = selectedWorkPeriod?.start_date ? safeParseDateInput(selectedWorkPeriod.start_date) : null;
    const parsedEnd = selectedWorkPeriod?.end_date ? safeParseDateInput(selectedWorkPeriod.end_date) : null;
    const periodStart = parsedStart ? parsedStart.format('YYYY-MM-DD') : dataFirstDate;
    const periodEnd = parsedEnd ? parsedEnd.format('YYYY-MM-DD') : dataLastDate;
    const today = dayjs().format('YYYY-MM-DD');
    const clamped = today < periodStart ? periodStart : today > periodEnd ? periodEnd : today;
    if (sortedDates.includes(clamped)) return clamped;
    const preceding = sortedDates.filter((date) => date <= clamped);
    return preceding[preceding.length - 1] ?? dataFirstDate;
  }, [chartData, selectedWorkPeriod]);

  /** Effective date driving cards, subtitle, chart cutoff: user pick or anchorDate fallback. */
  const effectiveSelectedDate = selectedDate ?? anchorDate;

  /**
   * Date dropdown options derived from chart-data dates, sorted descending so
   * the most recent date appears at the top (mirrors Work Phase Distribution).
   * Future dates are kept in the list because the user explicitly wants WPD
   * parity for the picker; metric values stay safe because the card / chart
   * paths read through `cardDate`, which caps at today.
   */
  const dateOptions = useMemo(() => {
    if (!chartData.length) return [];
    return chartData
      .map((point) => point.date)
      .filter((date): date is string => Boolean(date))
      .sort()
      .reverse()
      .map((date) => ({
        value: date,
        label: formatDate(date, { format: 'MMM D, YYYY' }),
      }));
  }, [chartData]);

  /**
   * Metric-anchored date used by the cards, the chart's side metrics, and the
   * Done drill-in. When the picker is at today-or-earlier, this equals the
   * pick. When the picker is in the future, this falls back to `anchorDate`
   * — NOT to literal today — because today may not be in chartData (weekend /
   * data-ingestion lag) and the metric helpers short-circuit to zero when
   * they can't find the date in chartData. `anchorDate` is already snapped to
   * the latest data-point on or before today, so it's guaranteed lookup-safe.
   */
  const cardDate = useMemo<string | null>(() => {
    if (!effectiveSelectedDate) return null;
    const today = dayjs().format('YYYY-MM-DD');
    return effectiveSelectedDate > today ? anchorDate : effectiveSelectedDate;
  }, [effectiveSelectedDate, anchorDate]);

  /**
   * Visualization-only copy of `chartData` with the Actual / Ideal / Total
   * line-series fields nullified for any data point strictly after the chart
   * cutoff. Cutoff is `min(effectiveSelectedDate, today)` so the chart never
   * shows lines past today even if the user picks a future date (mirrors
   * `work-phase-distribution-chart.tsx` filteredChartData). The x-axis still
   * renders future labels — only the lines stop drawing.
   */
  const displayChartData = useMemo<PaceChartDataPoint[]>(() => {
    if (!chartData.length) return chartData;
    const today = dayjs().endOf('day');
    const selectedDayjs = effectiveSelectedDate ? dayjs(effectiveSelectedDate).endOf('day') : null;
    const cutoff = !selectedDayjs || selectedDayjs.isAfter(today) ? today : selectedDayjs;
    return chartData.map((point) => {
      if (!point.date) return point;
      if (!dayjs(point.date).isAfter(cutoff)) return point;
      return {
        ...point,
        actual: null as unknown as number,
        ideal: null as unknown as number,
        remaining: null as unknown as number,
        complete: null as unknown as number,
        total: null as unknown as number,
      };
    });
  }, [chartData, effectiveSelectedDate]);

  // Flora summarizes the same lookup-safe date as the cards (`cardDate`) so it
  // never confabulates "0 of N tasks done on a future date" from the trailing
  // future-zero rows in chartData.
  const floraData = useMemo(
    () => formatWorkPaceDataForFlora(chartData, cardDate, selectedMetric, chartType, selectedWorkPeriod, i18n.t),
    [chartData, cardDate, selectedMetric, chartType, selectedWorkPeriod, i18n.t],
  );

  // chartType is a stable enum key, so this memo is stable across language
  // changes — only flips when the user toggles burn-up / burn-down.
  const floraPromptKey = useMemo(() => getWorkPaceFloraPromptKey(chartType), [chartType]);

  const [taskQuery, setTaskQuery] = useState<{
    filter: string;
    workPeriodId: string;
  } | null>(null);

  const { data: tasksQueryData = [], isFetching: isTasksFetching } = useWorkPeriodsTasksData(
    [Measure.FlowByPace],
    taskQuery?.filter,
    { enabled: !!taskQuery },
  );

  // Effect to trigger onTasksChange when tasks are loading
  useEffect(() => {
    if (taskQuery && isTasksFetching && onTasksChange) {
      const title = `Done - ${formatDate(taskQuery.filter.split(',')[1], { format: 'MMM D, YYYY' })}`;
      onTasksChange([], title, true);
    }
  }, [taskQuery, isTasksFetching, onTasksChange]);

  // Effect to trigger onTasksChange when tasks data is loaded
  useEffect(() => {
    if (taskQuery && !isTasksFetching && onTasksChange) {
      const title = `Done - ${formatDate(taskQuery.filter.split(',')[1], { format: 'MMM D, YYYY' })}`;
      onTasksChange(tasksQueryData as TaskRecord[], title, false);
    }
  }, [tasksQueryData, taskQuery, isTasksFetching, onTasksChange]);

  /**
   * Drills into the Done tasks list for a given date. When invoked from the
   * static PhaseBox card it defaults to `effectiveSelectedDate` (the date
   * currently shown in the picker / cards); when invoked from a chart dot it
   * receives that dot's date so the drawer shows tasks for the point the user
   * clicked on, independent of the picker.
   *
   * @param {string | null} dateArg - The date to query; falls back to the picker selection
   * @returns {void}
   */
  const handleDoneClick = (dateArg: string | null = cardDate) => {
    if (!dateArg) return;
    setClickedPhaseBox(false);
    setTaskQuery({
      filter: `done,${dateArg}`,
      workPeriodId: selectedWorkPeriod?.id || '',
    });
  };

  const handleExport = () => {
    if (!downloadRef.current) return;
    exportChart(downloadRef.current, `work-pace-${selectedMetric}.png`);
  };

  return (
    <div ref={downloadRef} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WorkPeriodChartHeader
        title="Work Pace"
        selectedWorkPeriod={selectedWorkPeriod}
        onExport={handleExport}
        floraButton={
          useAI && (
            <FloraButton
              externalData={[floraData]}
              prompt={i18n.t(floraPromptKey)}
              tooltipText={i18n.t('flora.work_periods.flora_button_tooltip')}
            />
          )
        }
      />
      <Group justify="space-between" align="center" mt={16} mb={16}>
        <Text weight="bold" color={skyDark}>
          {effectiveSelectedDate ? formatDate(effectiveSelectedDate, { format: 'MMM D, YYYY' }) : 'Work Pace'}
        </Text>
        <Group gap="xs">
          <Group gap="xs" role="group" aria-label={i18n.t('work_periods.chart_type_label')}>
            <Button
              radius="xl"
              size="xs"
              variant={chartType === WorkPaceChartType.BurnUp ? 'secondary' : 'outline'}
              onClick={() => setChartType(WorkPaceChartType.BurnUp)}
              aria-pressed={chartType === WorkPaceChartType.BurnUp}
              leftSection={
                <Icon
                  name="trending_up"
                  color={chartType === WorkPaceChartType.BurnUp ? baseWhite : secondaryBase}
                  size={16}
                />
              }
            >
              {i18n.t('work_periods.burn_up')}
            </Button>
            <Button
              radius="xl"
              size="xs"
              variant={chartType === WorkPaceChartType.BurnDown ? 'secondary' : 'outline'}
              onClick={() => setChartType(WorkPaceChartType.BurnDown)}
              aria-pressed={chartType === WorkPaceChartType.BurnDown}
              leftSection={
                <Icon
                  name="trending_down"
                  color={chartType === WorkPaceChartType.BurnDown ? baseWhite : secondaryBase}
                  size={16}
                />
              }
            >
              {i18n.t('work_periods.burn_down')}
            </Button>
          </Group>
          <Divider orientation="vertical" />
          <Select
            value={effectiveSelectedDate}
            options={dateOptions}
            onChange={setSelectedDate}
            placeholder={
              dateOptions.length === 0
                ? i18n.t('work_periods.no_dates_available')
                : i18n.t('work_periods.date_picker_placeholder')
            }
            size="md"
            radius="xl"
          />
        </Group>
      </Group>
      {isLoading && !data && <VisuallyHiddenStatus role="status">Loading work pace summary</VisuallyHiddenStatus>}
      <PhaseBoxContainer aria-busy={isLoading && !data}>
        {isLoading && !data ? (
          <>
            <Skeleton height={90} width={120} radius={16} />
            <Skeleton height={90} width={120} radius={16} />
            <Skeleton height={90} width={120} radius={16} />
          </>
        ) : (
          <>
            <PhaseBox
              name="Burnt"
              color={secondaryBase}
              description="Time elapsed in the work period"
              value={getWorkPeriodBurnedMetadata(chartData, cardDate).percent}
              isPercentage={true}
            />
            <PhaseBox
              name={chartType === WorkPaceChartType.BurnDown ? 'Remaining' : 'Done'}
              color={secondaryBase}
              description={
                chartType === WorkPaceChartType.BurnDown
                  ? 'Work remaining in the work period'
                  : 'Work completed in the work period'
              }
              value={
                chartType === WorkPaceChartType.BurnDown
                  ? 100 - getTasksCompleteMetadata(chartData, cardDate, selectedMetric, i18n.t).percent
                  : getTasksCompleteMetadata(chartData, cardDate, selectedMetric, i18n.t).percent
              }
              isPercentage={true}
              onClick={() => {
                handleDoneClick();
                setClickedPhaseBox(true);
              }}
              isSelected={tasksOpen && clickedPhaseBox}
            />
            <PhaseBox
              name="Scope"
              color={secondaryBase}
              description="Scope change since the start of the work period"
              value={getScopeMetadata(chartData, cardDate, selectedMetric, i18n.t).percent}
              isPercentage={true}
            />
          </>
        )}
      </PhaseBoxContainer>
      <ChartScope>
        <BrandedLoadingOverlay variant="colored" visible={isLoading} scoped />
        <WorkPaceChart
          chartType={chartType}
          chartData={displayChartData}
          selectedDate={cardDate}
          handleDoneClick={handleDoneClick}
        />
      </ChartScope>
    </div>
  );
};

const ChartScope = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
`;

// Visually-hidden status announcer for the phase-box skeletons. Lives outside
// the layout flow so screen readers receive a `role="status"` live region
// without polluting the visual flex row of `PhaseBoxContainer`. Uses the
// classic clip-path/inset pattern so the element remains in the accessibility
// tree but contributes zero pixels.
const VisuallyHiddenStatus = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
